import { KeyboardEvent, ReactNode } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { sentenceParts } from '../automation-sentence';

/// Peças da tela de automações (lista e painel da regra), iguais para regras gerais e de coluna.

const useColors = () => {
    const [controller] = useMaterialUIController();
    return getCrmColors(controller.darkMode);
};

const onEnter = (action: () => void) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        action();
    }
};

interface SectionProps {
    title: string;
    /// Divisória acima: separa uma seção da anterior.
    divided?: boolean;
    children: ReactNode;
}

export const ListSection = ({ title, divided, children }: SectionProps) => {
    const cc = useColors();

    return (
        <Box sx={{ pt: 3, pb: 2, borderTop: divided ? `1px solid ${cc.divider}` : 'none' }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: cc.textPrimary, px: 3 }}>
                {title}
            </Typography>

            <Box sx={{ mt: 1.75, display: 'grid', gap: 0.5, px: 1.5 }}>{children}</Box>
        </Box>
    );
};

/// Nome da coluna acima das regras dela, só onde há regra.
export const ListGroupLabel = ({ children }: { children: ReactNode }) => {
    const cc = useColors();

    return (
        <Typography
            sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                color: cc.textMuted,
                px: 1.5,
                pt: 2,
                pb: 0.75,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {children}
        </Typography>
    );
};

interface ItemProps {
    icon: ReactNode;
    title: string;
    selected: boolean;
    onSelect: () => void;
    /// Sem interruptor quando o item é um grupo, como "Quando o lead chega".
    enabled?: boolean;
    onToggle?: () => void;
    /// Canto direito quando não há interruptor.
    trailing?: ReactNode;
}

export const RuleListItem = ({
    icon, title, enabled = true, selected, onSelect, onToggle, trailing,
}: ItemProps) => {
    const cc = useColors();

    return (
        <Box
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            onClick={onSelect}
            onKeyDown={onEnter(onSelect)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                pl: 1.5,
                pr: 0.75,
                minHeight: 48,
                borderRadius: '10px',
                cursor: 'pointer',
                bgcolor: selected ? cc.accentSoftBg : 'transparent',
                '&:hover': { bgcolor: selected ? cc.accentSoftBg : cc.countBg },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    color: selected ? cc.accent : cc.textSecondary,
                    '& .MuiSvgIcon-root': { fontSize: 21 },
                }}
            >
                {icon}
            </Box>

            <Typography
                sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 15,
                    fontWeight: 500,
                    color: selected ? cc.onTint : enabled ? cc.textPrimary : cc.textSecondary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {title}
            </Typography>

            {/* O clique no interruptor liga e desliga sem trocar o painel. */}
            {onToggle ? (
                <Switch
                    checked={enabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={onToggle}
                />
            ) : (
                <Box sx={{ pr: 1.25, minHeight: 38, display: 'flex', alignItems: 'center' }}>{trailing}</Box>
            )}
        </Box>
    );
};

/// Botão de texto no fim da lista.
export const ListAction = ({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) => {
    const cc = useColors();

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={onEnter(onClick)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                minHeight: 44,
                mt: 1,
                borderRadius: '10px',
                border: `1px dashed ${cc.borderStrong}`,
                cursor: 'pointer',
                fontSize: 14.5,
                fontWeight: 600,
                color: cc.accent,
                '& .MuiSvgIcon-root': { fontSize: 21 },
                '&:hover': { bgcolor: cc.countBg, borderColor: cc.accent },
            }}
        >
            {icon}
            {label}
        </Box>
    );
};

/// A frase da regra, com o que foi escolhido em negrito.
export const SentenceText = ({ sentence, size = 14.5 }: { sentence: string; size?: number }) => {
    const cc = useColors();

    return (
        <Typography component="div" sx={{ fontSize: size, lineHeight: 1.6, color: cc.textSecondary }}>
            {sentenceParts(sentence).map((part, index) =>
                part.strong ? (
                    <Box key={index} component="span" sx={{ color: cc.textPrimary, fontWeight: 600 }}>
                        {part.text}
                    </Box>
                ) : (
                    <span key={index}>{part.text}</span>
                ))}
        </Typography>
    );
};

interface PanelProps {
    icon: ReactNode;
    title: string;
    /// A frase da regra. Sem ela, o subtítulo em texto corrido.
    sentence?: string;
    subtitle?: string;
    /// Sem isto não há interruptor: regra ainda não criada nasce ligada.
    enabled?: boolean;
    onToggle?: () => void;
    /// Ao lado do interruptor, como excluir.
    actions?: ReactNode;
    footer?: ReactNode;
    children?: ReactNode;
}

export const RulePanel = ({
    icon, title, sentence, subtitle, enabled, onToggle, actions, footer, children,
}: PanelProps) => {
    const { t } = useTranslation();
    const cc = useColors();

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${cc.border}`,
                borderRadius: '10px',
                background: cc.surfaceBg,
                overflow: 'hidden',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3.5, py: 3, flexShrink: 0 }}>
                <Box
                    sx={{
                        width: 46,
                        height: 46,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: cc.accentSoftBg,
                        color: cc.accent,
                        flexShrink: 0,
                        '& .MuiSvgIcon-root': { fontSize: 23 },
                    }}
                >
                    {icon}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 19, fontWeight: 700, color: cc.textPrimary, lineHeight: 1.35 }}>
                        {title}
                    </Typography>
                    {sentence ? (
                        <SentenceText sentence={sentence} />
                    ) : (
                        <Typography sx={{ fontSize: 14.5, color: cc.textSecondary }}>{subtitle}</Typography>
                    )}
                </Box>

                {actions}

                {onToggle && enabled !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: enabled ? cc.textPrimary : cc.textMuted }}>
                            {enabled ? t('automacoes.ligada') : t('automacoes.desligada')}
                        </Typography>
                        <Switch checked={enabled} onChange={onToggle} />
                    </Box>
                )}
            </Box>

            {children && (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        alignContent: 'start',
                        px: 3.5,
                        pt: 3,
                        pb: 3.5,
                        display: 'grid',
                        gap: 3,
                        borderTop: `1px solid ${cc.divider}`,
                    }}
                >
                    {children}
                </Box>
            )}

            {footer && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexShrink: 0,
                        mt: 'auto',
                        gap: 1.5,
                        px: 3.5,
                        py: 2.25,
                        borderTop: `1px solid ${cc.divider}`,
                    }}
                >
                    {footer}
                </Box>
            )}
        </Box>
    );
};

interface BlockProps {
    icon: ReactNode;
    title: string;
    sentence: string;
    enabled: boolean;
    onToggle: () => void;
    /// Configuração da regra, embaixo da linha.
    children?: ReactNode;
}

/// Uma regra no painel de um grupo: título, frase e interruptor numa linha, e a configuração embaixo.
export const RuleBlock = ({ icon, title, sentence, enabled, onToggle, children }: BlockProps) => {
    const cc = useColors();

    return (
        <Box sx={{ border: `1px solid ${cc.border}`, borderRadius: '10px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2.25 }}>
                <Box
                    sx={{
                        display: 'flex',
                        color: enabled ? cc.accent : cc.textMuted,
                        '& .MuiSvgIcon-root': { fontSize: 22 },
                    }}
                >
                    {icon}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: cc.textPrimary, mb: 0.25 }}>
                        {title}
                    </Typography>
                    <SentenceText sentence={sentence} />
                </Box>

                <Switch checked={enabled} onChange={onToggle} />
            </Box>

            {children && (
                <Box sx={{ px: 3, py: 3, display: 'grid', gap: 3, borderTop: `1px solid ${cc.divider}` }}>
                    {children}
                </Box>
            )}
        </Box>
    );
};
